import { reactive } from "@vue/composition-api";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";
import { WorkOrderDetailed } from "@/store/modules/workOrders";

import { buildFormCommodityAutocomplete } from "@/models/Commodity";
import { buildFormClientFieldAutocomplete } from "@/models/ClientField";

import { buildFormTemplateWorkOrderAutocomplete } from "@/models/TemplateWorkOrder";

export const enum WorkOrderStatus {
  pending = "pending",
  queued = "queued",
  completed = "completed",
  billed = "billed",
  archived = "archived",
}

const prettifiedWorkOrderStatus = {
  pending: "Pending",
  queued: "Queued",
  completed: "Completed",
  billed: "Billed",
  archived: "Archived",
};

export const prettifyWorkOrderStatus = (s: WorkOrderStatus) => {
  return prettifiedWorkOrderStatus[s];
};

export const workOrderStatusReverseLookup = <
  { [key: string]: WorkOrderStatus }
>{
  pending: WorkOrderStatus.pending,
  queued: WorkOrderStatus.queued,
  completed: WorkOrderStatus.completed,
  billed: WorkOrderStatus.billed,
  archived: WorkOrderStatus.archived,
};

export interface FormWorkOrder {
  id: string;
  name: string;
  description: string;
  start_date: string;
  submitted_date: string;
  completed_date: string;
  scheduled_date: string;
  archived_date: string;
  status: string;
  client_field_id: string;
  acres: number;
  commodity_id: string;
  duration: number;
  duration_unit: string;
  cut_number: number;
  formCommodity: AutocompleteResult;
  formClientField: AutocompleteResult;
}

export interface FormCreateWorkOrder {
  submitted_date: string;
  client_field_id: string;
  template_id: string;
  work_order_id: string;
  formClientField: AutocompleteResult;
  formTemplateWorkOrder: AutocompleteResult;
}

export function buildFormCreateWorkOrder() {
  let { formClientField } = buildFormClientFieldAutocomplete();
  let { formTemplateWorkOrder } = buildFormTemplateWorkOrderAutocomplete();

  let formCreateWorkOrder = reactive<FormCreateWorkOrder>({
    submitted_date: "",
    client_field_id: "",
    template_id: "",
    work_order_id: "",
    formClientField: formClientField,
    formTemplateWorkOrder: formTemplateWorkOrder,
  });

  return {
    formCreateWorkOrder,
  };
}

export function buildFormWorkOrder() {
  let { formCommodity } = buildFormCommodityAutocomplete();
  let { formClientField } = buildFormClientFieldAutocomplete();

  let formWorkOrder = reactive<FormWorkOrder>({
    id: "",
    name: "",
    description: "",
    start_date: "",
    submitted_date: "",
    completed_date: "",
    scheduled_date: "",
    archived_date: "",
    status: "",
    client_field_id: "",
    acres: 0,
    commodity_id: "",
    duration: 0,
    duration_unit: "",
    cut_number: 0,
    formCommodity: formCommodity,
    formClientField: formClientField,
  });

  let loadWorkOrderIntoForm = (wo: WorkOrderDetailed, fwo: FormWorkOrder) => {
    fwo.id = wo.id;
    fwo.name = wo.name;
    fwo.description = wo.description;
    fwo.start_date = wo.start_date;
    fwo.submitted_date = wo.submitted_date;
    fwo.completed_date = wo.completed_date;
    fwo.scheduled_date = wo.scheduled_date;
    fwo.archived_date = wo.archived_date;
    fwo.status = wo.status;
    fwo.client_field_id = wo.client_field_id;
    fwo.acres = wo.acres;
    fwo.commodity_id = wo.commodity_id;
    fwo.duration = wo.duration;
    fwo.duration_unit = wo.duration_unit;
    fwo.cut_number = wo.cut_number;

    fwo.formCommodity.label = wo.commodity;
    fwo.formCommodity.id = wo.commodity_id;

    fwo.formClientField.label = wo.client_field.name;
    fwo.formClientField.id = wo.client_field_id;
  };

  let workOrderStatuses = reactive<WorkOrderStatus[]>([]);

  const loadWorkOrderStatusesIntoForm = (woStatuses: WorkOrderStatus[]) => {
    woStatuses.push(
      WorkOrderStatus.pending,
      WorkOrderStatus.queued,
      WorkOrderStatus.completed,
      WorkOrderStatus.billed,
      WorkOrderStatus.archived,
    );
  };

  return {
    workOrderStatuses,
    loadWorkOrderStatusesIntoForm,
    formWorkOrder,
    loadWorkOrderIntoForm,
  };
}
